import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { useAddPlayersToSeries } from '@/hooks/use-director-data';

interface AddPlayerSheetProps {
  sessionId: string;
  seriesId?: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function AddPlayerSheet({
  sessionId,
  seriesId,
  open,
  onOpenChange,
}: AddPlayerSheetProps) {
  const available = useQuery(
    api.director.sessions.getAvailablePlayers,
    open ? { sessionId: sessionId as Id<'trainingSessions'> } : 'skip'
  );
  const addPlayers = useMutation(api.director.sessions.addPlayers);
  const addToSeries = useAddPlayersToSeries();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [addToAllInSeries, setAddToAllInSeries] = useState(false);

  const filtered = useMemo(() => {
    if (!available) return [];
    const q = search.toLowerCase();
    return available.filter((p) =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q)
    );
  }, [available, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      const playerIds = Array.from(selected);
      if (addToAllInSeries && seriesId) {
        await addToSeries.mutateAsync({ seriesId, playerIds });
      } else {
        await addPlayers({
          sessionId: sessionId as Id<'trainingSessions'>,
          playerIds: playerIds as Id<'players'>[],
        });
      }
      setSelected(new Set());
      setAddToAllInSeries(false);
      onOpenChange(false);
    } finally {
      setAdding(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Add Players to Session</SheetTitle>
        </SheetHeader>

        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <Input
            placeholder="Search players..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex-1 overflow-y-auto mt-3 space-y-2">
          {available === undefined ? (
            [1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              {(available ?? []).length === 0 ? 'All players already added.' : 'No players match your search.'}
            </p>
          ) : (
            filtered.map((player) => {
              const isSelected = selected.has(player.id);
              return (
                <button
                  key={player.id}
                  onClick={() => toggle(player.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    isSelected
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 flex items-center justify-center text-sm font-medium shrink-0">
                    {player.firstName?.[0] ?? '?'}{player.lastName?.[0] ?? ''}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                      {player.firstName} {player.lastName}
                    </p>
                    {player.level && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{player.level}</p>
                    )}
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>

        <div className="pt-4 border-t space-y-3">
          {seriesId && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={addToAllInSeries}
                onChange={(e) => setAddToAllInSeries(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span>Add to all sessions in this series</span>
            </label>
          )}
          <Button
            className="w-full"
            disabled={selected.size === 0 || adding}
            onClick={handleAdd}
          >
            {adding ? 'Adding...' : `Add ${selected.size > 0 ? selected.size : ''} Player${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
